import type { HTMLAttributes } from 'react';
import { useEffect, useState } from 'react';

import type { CSSObject, MantineTheme } from '@mantine/core';
import { Box, Group, Text, useMantineTheme } from '@mantine/core';
import { useHover } from '@mantine/hooks';

import { IconEye, IconEyeClosed } from '@tabler/icons-react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { Avatar } from '../../../../../../../../../shared/components/UI/Avatar/Avatar';
import { Badge } from '../../../../../../../../../shared/components/UI/Badge/Badge';
import { AvatarWithInitials } from '../../../../../../../../../shared/components/UI/UserWithAvatar/UserWithAvatar';

import { GradientBorder } from '../../../../../../../../../shared/components/GradientBorder';
import type {
  AssistantLlmMessage,
  AssistantThreadMessage,
  AssistantUserMessage,
} from '../../../../../../../../../shared/entities/AssistantThreadMessage';
import { AssistantThreadMessageRole } from '../../../../../../../../../shared/entities/AssistantThreadMessage';
import type { DocumentChunk } from '../../../../../../../../../shared/entities/DocumentChunk';
import { useSourceSidePanel } from '../../../../../../../../documents/hooks/useSourceSidePanel';
import type {
  AIChatContentSegment,
  AIChatContentSourceSegment,
} from '../../../../../../assistant/types/aiChatContentSegment.types';
import AssistantAvatar from '../assets/AssistantAvatar.svg?react';

export default function ThreadMessage({
  message,
}: {
  message: AssistantThreadMessage;
}) {
  if (message.role === AssistantThreadMessageRole.USER) {
    return <UserMessage message={message} />;
  }

  if (message.role === AssistantThreadMessageRole.ASSISTANT) {
    return <LlmMessage message={message} />;
  }

  const exhaustiveCheck: never = message;
  throw new Error(`Unknown message role: ${(exhaustiveCheck as any).role}`);
}

function UserMessage({ message }: { message: AssistantUserMessage }) {
  return (
    <Group position="right" align="start" spacing="02" noWrap>
      <Box mr="100px" h="100%" />

      <AvatarWithInitials
        firstName={message.owner.firstName}
        lastName={message.owner.lastName}
        avatarColor={message.owner.avatarColor}
      />
      <BaseMessage content={message.content} isUserMessage={true} />
    </Group>
  );
}

function LlmMessage({ message }: { message: AssistantLlmMessage }) {
  const theme = useMantineTheme();

  return (
    <Group align="start" spacing="02" noWrap>
      <Avatar component={AssistantAvatar} />
      <GradientBorder
        borderRadius={theme.radius.md}
        gradient="linear-gradient(355deg, #FFF 56.43%, #E5F2FE 98.25%)"
      >
        <BaseMessage content={message.content} />
      </GradientBorder>
      <Box mr="100px" h="100%" />
    </Group>
  );
}

type BaseMessageProps = {
  content: AIChatContentSegment[];
  isUserMessage?: boolean;
};

function BaseMessage({ content, isUserMessage = false }: BaseMessageProps) {
  const formattedContent = formatSegments(content);

  function createMessageStyle(theme: MantineTheme): CSSObject {
    if (isUserMessage) {
      return {
        backgroundColor: theme.colors.primary[0],
        border: `1px solid ${theme.colors.primary[2]}`,
      };
    }

    return {};
  }

  return (
    <Text
      c="gray.9"
      size="sm"
      fw={400}
      sx={theme => ({
        padding: `${theme.spacing['03']} ${theme.spacing['04']}`,
        borderRadius: theme.radius.md,
        ...createMessageStyle(theme),
        '& > p:first-of-type': { marginTop: '0' },
        '& > p:last-of-type': { marginBottom: '0' },
      })}
    >
      <Markdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          code: ({ children }: HTMLAttributes<HTMLElement>) => {
            try {
              const aIChatContentSourceSegment = JSON.parse(
                String(children),
              ) as AIChatContentSourceSegment;

              return (
                <SourceBadge
                  documentChunk={aIChatContentSourceSegment.content}
                  S3FileURL={aIChatContentSourceSegment.S3FileURL}
                  S3PreviewFileURL={aIChatContentSourceSegment.S3PreviewFileURL}
                  prettyName={aIChatContentSourceSegment.prettyName}
                />
              );
            } catch (err) {
              console.error('Invalid data-content JSON', err);
              return null;
            }
          },
        }}
      >
        {formattedContent}
      </Markdown>
    </Text>
  );
}

function SourceBadge({
  documentChunk,
  S3FileURL,
  S3PreviewFileURL,
  prettyName,
}: {
  documentChunk: DocumentChunk;
  S3FileURL: string;
  S3PreviewFileURL: string;
  prettyName: string;
}) {
  const { hovered, ref } = useHover();
  const { openPanel } = useSourceSidePanel({
    sources: [documentChunk],
    documents: [{ S3FileURL, S3PreviewFileURL, prettyName }],
  });

  return (
    <Badge
      ref={ref}
      size="xs"
      rightSection={<BlinkingEyeIcon hovered={hovered} />}
      py="02"
      mr="01"
      sx={theme => ({
        cursor: 'pointer',
        border: `1px solid ${theme.colors.gray[2]}`,
        ':hover': {
          backgroundColor: theme.colors.gray[1],
        },
      })}
      onClick={() => openPanel(documentChunk)}
    >
      <Text
        size="10px"
        fw="600"
        color="gray.7"
        mt="00"
        truncate
        sx={{ textTransform: 'none', maxWidth: 215 }}
      >
        {documentChunk.metadata.filePath}
      </Text>
    </Badge>
  );
}

function BlinkingEyeIcon({ hovered }: { hovered: boolean }) {
  const [isOpen, setIsOpen] = useState(true);
  const delay = 200;

  useEffect(() => {
    if (!hovered) {
      setIsOpen(true);
      return;
    }

    const blinkSequence = async () => {
      setIsOpen(false);
      await new Promise(resolve => setTimeout(resolve, delay));
      setIsOpen(true);
      await new Promise(resolve => setTimeout(resolve, delay));
    };

    blinkSequence();
  }, [hovered]);

  useEffect(() => {
    const blinkSequence = async () => {
      setIsOpen(false);
      await new Promise(resolve => setTimeout(resolve, delay));
      setIsOpen(true);
    };

    const scheduleNextBlink = () => {
      // Random interval between 2 and 5 seconds
      const randomInterval = Math.random() * 8000 + 2000;

      return setTimeout(() => {
        blinkSequence();
        scheduleNextBlink();
      }, randomInterval);
    };

    // Start the blinking sequence
    const timeoutId = scheduleNextBlink();

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: 14,
        height: 14,
        cursor: 'pointer',
        '& > svg': {
          position: 'absolute',
          top: 0,
          left: 0,
          transition: 'opacity 0.15s ease-in-out, transform 0.15s ease-in-out',
        },
      }}
    >
      <IconEye
        size={14}
        style={{
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'scale(1)' : 'scale(0.8)',
        }}
      />
      <IconEyeClosed
        size={14}
        style={{
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'scale(0.8)' : 'scale(1)',
        }}
      />
    </Box>
  );
}

function formatSegments(segments: AIChatContentSegment[]): string {
  return segments
    .map(segment => {
      if (segment.type === 'text') {
        return segment.content;
      }
      if (segment.type === 'source') {
        const json = JSON.stringify(segment);
        return `<code>${json}</code>`;
      }
      return null;
    })
    .filter(Boolean)
    .join('');
}

ThreadMessage.User = UserMessage;
ThreadMessage.Llm = LlmMessage;
